import React, { useState } from "react";
import cn from "classnames";
import styles from "./style.scss";
import { Chat, DownloadType, Reference } from "models/AiResearch";
import { Recommendation } from "features/AiResearch/Components/Recommendation";
import { AiResearchText } from "components/AiResearch/AiResearchText";
import { References } from "components/AiResearch/References";
import { useReduxState } from "hooks/useReduxState";
import ReportImage from "images/icon/report_outline_rounded.svg";
import { UserQuestion } from "./UserQuestion";
import { DownloadButton } from "components/AiResearch/DownloadButton";
import { CopyButton } from "components/AiResearch/CopyButton";
import { trackAction } from "utils/helper/analytics";

interface Props {
  chat: Chat;
  selectedReference?: Reference | null;
  handleSelectRecommendation: (text: string) => void;
  handleShowReference: ({
    questionId,
    referenceId
  }: {
    questionId: string;
    referenceId: number;
  }) => void;
  handleCopy: ({ chatIndex }: { chatIndex: number }) => void;
  handleDownload: ({
    type,
    questionId
  }: {
    type: DownloadType;
    questionId: string;
  }) => void;
}

export const Dialog: React.FC<Props> = ({
  chat,
  selectedReference,
  handleSelectRecommendation,
  handleShowReference,
  handleCopy,
  handleDownload
}) => {
  const [showDownloadPulldown, setShowDownloadPulldown] = useState(false);
  const { trialFlg } = useReduxState(state => state.auth.saved.user);

  const onClickDownloadPulldown = ({
    type,
    questionId
  }: {
    type: DownloadType;
    questionId: string;
  }) => {
    setShowDownloadPulldown(!showDownloadPulldown);
    handleDownload({ type, questionId });
  };

  // チャット履歴の最後の質問
  const lastChat = chat.history[chat.history.length - 1];

  // 関連質問一覧
  const relatedQuestions = lastChat?.relatedQuestions;

  // 「こんな質問はいかがですか？」表示フラグ
  // - 回答生成中は表示
  // - エラー発生時は非表示
  // - 生成完了かつ関連質問が0件は非表示
  // - 生成完了かつ関連質問がある場合は表示
  const isShowRecommendation =
    !lastChat?.isError &&
    (!!lastChat?.isTextGenerating ||
      (lastChat?.sseStatus === "closed" &&
        (relatedQuestions?.length ?? 0) > 0));

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {chat.history.length > 0 &&
          chat.history.map((item, index) => {
            const isShowReference =
              (item.reference?.length || item.isTextGenerating) &&
              !item.isError;

            // コピー・ダウンロード機能制限フラグ
            const copyDownloadDisabled = trialFlg || !!item.isTextGenerating;

            return item.isError ? (
              <React.Fragment key={index}>
                <UserQuestion
                  question={item.question}
                  questionOptimization={item.questionOptimization}
                  questionAt={item.questionAt}
                />
                <div className={styles.answerWrapper}>
                  <div className={styles.errorMessage}>
                    {item.answer !== "" && (
                      <>
                        <img src={ReportImage} alt="チャットエラー" />
                        <p>{item.answer}</p>
                      </>
                    )}
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <React.Fragment key={index}>
                <UserQuestion
                  question={item.question}
                  questionOptimization={item.questionOptimization}
                  questionAt={item.questionAt}
                />
                {item.answer ? (
                  <AiResearchText
                    text={item.answer}
                    selectedReference={selectedReference}
                    references={item.reference}
                    onClickReference={referenceId => {
                      handleShowReference({
                        questionId: item.ids?.questionId || "",
                        referenceId
                      });
                      const ref = item.reference?.find(
                        item => item.citationNumber === referenceId
                      );
                      if (ref) {
                        trackAction("clickReferenceNumberBtn", {
                          citationNumber: ref.citationNumber,
                          dataType: ref.dataType,
                          articleId:
                            ref.dataType === "article_chunk"
                              ? ref.data.articleId
                              : ref.data.id
                        });
                      }
                    }}
                  />
                ) : (
                  <div className={styles.loadingWrapper}>
                    <span className={styles.loadingText}>{item.progress}</span>
                    <div className={styles.skeleton}>
                      <span className={cn(styles.text, styles.top)} />
                      <span className={cn(styles.text, styles.bottom)} />
                    </div>
                  </div>
                )}

                {isShowReference && (
                  <>
                    <hr className={styles.referenceHr} />
                    <References
                      references={item.reference || []}
                      isTextGenerating={!!item.isTextGenerating}
                      onShowReference={number => {
                        handleShowReference({
                          questionId: item.ids?.questionId || "",
                          referenceId: number
                        });

                        const ref = item.reference?.find(
                          item => item.citationNumber === number
                        );
                        if (ref) {
                          trackAction("clickReferenceDetailBtn", {
                            citationNumber: ref.citationNumber,
                            dataType: ref.dataType,
                            articleId:
                              ref.dataType === "article_chunk"
                                ? ref.data.articleId
                                : ref.data.id
                          });
                        }
                      }}
                    />
                    <p className={styles.tipsWrapper}>
                      生成AIの回答は必ずしも正しいとは限りません。正確な情報はソースを確認してください。
                      <br />
                      コピー・ダウンロード時にグラフは含まれません。
                    </p>
                    <div className={styles.downloadWrapper}>
                      <CopyButton
                        onClick={() => {
                          trackAction("clickCopyAiResult");
                          handleCopy({ chatIndex: index });
                        }}
                        disabled={copyDownloadDisabled}
                      />
                      <DownloadButton
                        onClickPulldownItem={type => {
                          trackAction("clickDownloadAiResult", {
                            type: type === "docx" ? "word" : type
                          });
                          onClickDownloadPulldown({
                            type,
                            questionId: item.ids?.questionId || ""
                          });
                        }}
                        threshold={300}
                        disabled={copyDownloadDisabled}
                      />
                    </div>
                  </>
                )}
              </React.Fragment>
            );
          })}
        {isShowRecommendation ? (
          <div className={styles.recommendationWrapper}>
            <Recommendation
              recommendation={relatedQuestions || []}
              isLoading={lastChat?.sseStatus !== "closed"}
              onClickTips={text => {
                handleSelectRecommendation(text);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
